import React, { useEffect, useState, useRef } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api, fileUrl } from "../lib/api";
import { apiGetOffline } from "../lib/offline";
import { useAuth } from "../context/AuthContext";
import Spinner from "../components/Spinner";
import BlockRenderer from "../components/BlockRenderer";
import { ChevronLeft, ChevronDown, ChevronUp, CheckCircle2, FileText, ListChecks, PlayCircle, MessageCircle, BookOpen, Upload, Send, HelpCircle, X, Lock, Clock, Sparkles, Star } from "lucide-react";
import { useSetHelpMessage } from "../context/HelpContext";

const TABS = ["contenu", "note", "annexes", "commentaires"];

const normalizeQuiz = (quiz) => {
  const base = quiz && typeof quiz === "object" ? quiz : {};
  const questions = Array.isArray(base.questions) ? base.questions : [];

  return {
    passingScore: Number.isFinite(Number(base.passingScore)) ? Number(base.passingScore) : 80,
    pagination: base.pagination !== false,
    questions: questions.map((question, questionIndex) => ({
      id: question.id || `q_${questionIndex}`,
      text: question.text || question.question || "",
      type: question.type === "multiple" ? "multiple" : "single",
      answers: (Array.isArray(question.answers) ? question.answers : []).map((answer, answerIndex) => ({
        id: answer.id || `a_${questionIndex}_${answerIndex}`,
        text: answer.text || "",
        isCorrect: Boolean(answer.isCorrect),
      })),
    })),
  };
};

const sameAnswerSet = (a = [], b = []) => {
  const left = [...a].sort();
  const right = [...b].sort();
  return left.length === right.length && left.every((value, index) => value === right[index]);
};

const getQuizResults = (quiz, selectedAnswers) => {
  const questions = quiz.questions || [];
  const details = questions.map((question) => {
    const correctIds = (question.answers || []).filter((answer) => answer.isCorrect).map((answer) => answer.id);
    const selectedIds = selectedAnswers[question.id] || [];
    return {
      questionId: question.id,
      correct: sameAnswerSet(selectedIds, correctIds),
      selectedIds,
      correctIds,
    };
  });

  const correctCount = details.filter((item) => item.correct).length;
  const score = questions.length ? Math.round((correctCount / questions.length) * 100) : 0;
  return { score, correctCount, total: questions.length, passed: score >= quiz.passingScore, details };
};

const formatUnlockDate = (value) => {
  if (!value) return "";
  try {
    return new Date(value).toLocaleDateString("fr-FR", { day: "2-digit", month: "long", year: "numeric" });
  } catch {
    return "";
  }
};

const lessonIsLocked = (modules, lessonId) => {
  const module = (modules || []).find((m) => (m.lessons || []).some((lesson) => lesson.lesson_id === lessonId));
  return Boolean(module?.locked);
};

const assignmentSettings = (lesson) => ({
  enable_rating: Boolean(lesson?.assignment_settings?.enable_rating),
  enable_private_discussion: Boolean(lesson?.assignment_settings?.enable_private_discussion),
  allow_multiple_files: Boolean(lesson?.assignment_settings?.allow_multiple_files),
  enable_round_trips: Boolean(lesson?.assignment_settings?.enable_round_trips),
});

const getSubmissionFiles = (submission, key = "file_urls", legacyKey = "file_url") => {
  const urls = Array.isArray(submission?.[key]) ? submission[key].filter(Boolean) : [];
  const legacy = submission?.[legacyKey];
  return legacy && !urls.includes(legacy) ? [legacy, ...urls] : urls;
};

function StarRating({ value }) {
  const rating = Number(value || 0);
  if (!rating) return null;
  return (
    <div className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-3 py-1 text-amber-700 border border-amber-100">
      {[1, 2, 3, 4, 5].map((index) => {
        const filled = rating >= index;
        const half = !filled && rating >= index - 0.5;
        return (
          <span key={index} className="relative inline-flex h-4 w-4">
            <Star className="h-4 w-4 text-amber-300" />
            {(filled || half) && (
              <span className="absolute inset-0 overflow-hidden" style={{ width: filled ? "100%" : "50%" }}>
                <Star className="h-4 w-4 fill-current text-amber-500" />
              </span>
            )}
          </span>
        );
      })}
      <span className="ml-1 text-xs font-bold">{rating}/5</span>
    </div>
  );
}

export default function CoursePlayer() {
  const { lessonId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [lesson, setLesson] = useState(null);
  const [course, setCourse] = useState(null);
  const [progress, setProgress] = useState({});
  const [comments, setComments] = useState([]);
  const [activeTab, setActiveTab] = useState("contenu");
  const [openModule, setOpenModule] = useState({});
  const [note, setNote] = useState("");
  const [comment, setComment] = useState("");
  const [lessonRating, setLessonRating] = useState(0);
  const [ratingSaved, setRatingSaved] = useState(false);
  const [submission, setSubmission] = useState(null);
  const [text, setText] = useState("");
  const [files, setFiles] = useState([]);
  const [assignmentMessage, setAssignmentMessage] = useState("");
  const [assignmentMessageFiles, setAssignmentMessageFiles] = useState([]);
  const [assignmentBusy, setAssignmentBusy] = useState(false);
  const fileInputRef = useRef(null);
  const messageFileInputRef = useRef(null);
  const [askHelpOpen, setAskHelpOpen] = useState(false);
  const [helpQuestion, setHelpQuestion] = useState("");
  const [helpSending, setHelpSending] = useState(false);
  const [quizAnswers, setQuizAnswers] = useState({});
  const [quizPage, setQuizPage] = useState(0);
  const [quizSubmitted, setQuizSubmitted] = useState(false);
  const [quizResult, setQuizResult] = useState(null);
  const [mobileShowProgram, setMobileShowProgram] = useState(false);

  useSetHelpMessage(
    lesson && course ? `Bonjour ! J'ai une question sur la leçon "${lesson.title}" du cours "${course.title}".` : null,
    [lesson?.title, course?.title]
  );

  const askCommunityHelp = async () => {
    if (!helpQuestion.trim()) return;
    setHelpSending(true);
    try {
      const { data } = await api.post(`/lessons/${lessonId}/ask-help`, { question: helpQuestion });
      navigate(`/community/app/threads/${data.thread_id}`);
    } catch (e) {
      alert(e.response?.data?.detail || "Erreur lors de l'envoi");
    } finally { setHelpSending(false); }
  };

  useEffect(() => {
    setActiveTab("contenu");
    setMobileShowProgram(false);
    setText(""); setFiles([]); setSubmission(null); setAssignmentMessage(""); setAssignmentMessageFiles([]);
    setQuizAnswers({}); setQuizPage(0); setQuizSubmitted(false); setQuizResult(null);
    apiGetOffline(`/lessons/${lessonId}`).then(async (r) => {
      setLesson(r.data);
      setNote(r.data.note || "");
      const publicCourse = await apiGetOffline(`/courses/${r.data.course_id}`);
      let courseForSidebar = publicCourse.data;
      // collect enriched enrollment data for drip-content sidebar
      try {
        const er = await apiGetOffline("/enrollments/my");
        const enr = er.data.find((e) => e.course?.course_id === r.data.course_id);
        if (enr?.course) {
          courseForSidebar = enr.course;
          setProgress({});
        }
      } catch (_) {}
      setCourse(courseForSidebar);
      // open the module containing this lesson
      const idx = (courseForSidebar.modules || []).findIndex((m) => (m.lessons || []).some((l) => l.lesson_id === lessonId));
      setOpenModule({ [idx]: true });
      // fetch comments
      api.get(`/lessons/${lessonId}/comments`).then((cmt) => setComments(cmt.data)).catch(() => {});
      api.get(`/lessons/${lessonId}/feedback`).then((fb) => {
        setLessonRating(Number(fb.data?.rating || 0));
        setRatingSaved(Boolean(fb.data?.rating));
      }).catch(() => {});
      // existing submission?
      api.get("/assignments/my").then((sr) => {
        const my = sr.data.find((s) => s.lesson_id === lessonId);
        if (my) setSubmission(my);
      }).catch(() => {});
    }).catch((err) => {
      if (err.response?.status === 403) {
        const detail = err.response?.data?.detail;
        const courseId = typeof detail === "object" ? detail.course_id : err.response?.data?.course_id;
        const message = typeof detail === "object" ? detail.message : detail;
        if (message) alert(message);
        navigate(`/courses/${courseId || ""}`);
      }
    });
  }, [lessonId, navigate]);

  const markComplete = async () => {
    await api.post(`/lessons/${lessonId}/complete`);
    setLesson((l) => ({ ...l, completed: true }));
  };

  const saveNote = async () => {
    await api.post(`/lessons/${lessonId}/note`, { note });
  };

  const saveLessonRating = async (rating) => {
    setLessonRating(rating);
    setRatingSaved(false);
    try {
      await api.post(`/lessons/${lessonId}/feedback`, { rating });
      setRatingSaved(true);
    } catch (e) {
      alert(e.response?.data?.detail || "Impossible d'enregistrer la note");
    }
  };

  const sendComment = async () => {
    if (!comment.trim()) return;
    const { data } = await api.post(`/lessons/${lessonId}/comments`, { text: comment });
    setComments((c) => [...c, data]);
    setComment("");
  };

  const uploadFile = async (f) => {
    const fd = new FormData();
    fd.append("file", f);
    const { data } = await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
    return data;
  };

  const handleFileChange = async (e) => {
    const selected = Array.from(e.target.files || []);
    if (!selected.length) return;
    const settings = assignmentSettings(lesson);
    setFiles(settings.allow_multiple_files ? selected : selected.slice(0, 1));
  };

  const submitAssignment = async () => {
    setAssignmentBusy(true);
    try {
      const uploaded = [];
      for (const f of files) {
        const up = await uploadFile(f);
        uploaded.push(up.url);
      }
      const { data } = await api.post(`/assignments/${lessonId}/submit`, {
        text_response: text || null,
        file_url: uploaded[0] || null,
        file_urls: uploaded,
      });
      setSubmission(data);
      setText(""); setFiles([]);
      if (fileInputRef.current) fileInputRef.current.value = "";
    } finally {
      setAssignmentBusy(false);
    }
  };

  const sendAssignmentMessage = async () => {
    if (!submission || (!assignmentMessage.trim() && assignmentMessageFiles.length === 0)) return;
    setAssignmentBusy(true);
    try {
      const uploaded = [];
      for (const f of assignmentMessageFiles) {
        const up = await uploadFile(f);
        uploaded.push(up.url);
      }
      const { data } = await api.post(`/assignments/${submission.submission_id}/messages`, {
        body: assignmentMessage,
        file_urls: uploaded,
      });
      setSubmission((current) => ({ ...current, messages: [...(current?.messages || []), data] }));
      setAssignmentMessage(""); setAssignmentMessageFiles([]);
      if (messageFileInputRef.current) messageFileInputRef.current.value = "";
    } finally {
      setAssignmentBusy(false);
    }
  };


  const toggleQuizAnswer = (question, answerId) => {
    if (quizSubmitted) return;
    setQuizAnswers((current) => {
      const currentForQuestion = current[question.id] || [];
      let nextForQuestion;

      if (question.type === "multiple") {
        nextForQuestion = currentForQuestion.includes(answerId)
          ? currentForQuestion.filter((id) => id !== answerId)
          : [...currentForQuestion, answerId];
      } else {
        nextForQuestion = [answerId];
      }

      return { ...current, [question.id]: nextForQuestion };
    });
  };

  const submitQuiz = async () => {
    const quiz = normalizeQuiz(lesson.quiz);
    const result = getQuizResults(quiz, quizAnswers);
    setQuizResult(result);
    setQuizSubmitted(true);

    if (result.passed) {
      try {
        await api.post(`/lessons/${lessonId}/complete`);
        setLesson((l) => ({ ...l, completed: true }));
      } catch (e) {
        console.error(e);
      }
    }
  };

  const resetQuiz = () => {
    setQuizAnswers({});
    setQuizPage(0);
    setQuizSubmitted(false);
    setQuizResult(null);
  };

  if (!lesson || !course) return <Spinner className="min-h-[60vh]" />;

  const flatLessons = (course.modules || []).flatMap((m) => (m.lessons || []).map((lesson) => ({ ...lesson, locked: Boolean(m.locked), module_unlock_at: m.unlock_at, days_until_unlock: m.days_until_unlock })));
  const idx = flatLessons.findIndex((l) => l.lesson_id === lessonId);
  const prev = idx > 0 ? flatLessons[idx - 1] : null;
  const next = idx < flatLessons.length - 1 ? flatLessons[idx + 1] : null;

  const ProgramSidebar = ({ mobile = false }) => (
    <aside className={`${mobile ? "block" : "hidden lg:block"} bg-white border-r border-emerald-100 p-4 lg:max-h-[calc(100vh-76px)] lg:overflow-y-auto lg:sticky lg:top-[76px]`}>
        <Link to={`/courses/${course.course_id}`} className="text-xs text-gray-500 inline-flex items-center gap-1 mb-4 hover:text-brand-green" data-testid="back-to-course">
          <ChevronLeft className="w-4 h-4" /> Retour à la fiche
        </Link>
        <p className="font-heading font-bold text-sm mb-4 line-clamp-2">{course.title}</p>
        {course.drip_enabled && (
          <div className="mb-4 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
            <p className="font-semibold flex items-center gap-1"><Clock className="w-3.5 h-3.5" /> Déblocage progressif activé</p>
            <p>Un nouveau module est débloqué tous les {course.drip_interval_days || 7} jours.</p>
          </div>
        )}
        <div className="space-y-2">
          {(course.modules || []).map((m, idx) => (
            <div key={m.module_id}>
              <button onClick={() => setOpenModule((s) => ({ ...s, [idx]: !s[idx] }))} className={`w-full flex items-center justify-between px-3 py-2 rounded-lg ${openModule[idx] ? "bg-brand-green text-white" : "bg-emerald-50 text-black"} font-semibold text-sm`} data-testid={`player-module-${idx}`}>
                <span className="text-left flex items-center gap-2">
                  {m.locked ? <Lock className="w-4 h-4 shrink-0" /> : m.is_newly_unlocked ? <Sparkles className="w-4 h-4 shrink-0" /> : null}
                  <span>{m.title}</span>
                  {m.is_newly_unlocked && <span className="rounded-full bg-brand-orange px-2 py-0.5 text-[10px] font-black text-white">Nouveau</span>}
                </span>
                {openModule[idx] ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
              </button>
              {openModule[idx] && (
                <div className="mt-1 space-y-0.5">
                  {(m.lessons || []).map((l, li) => {
                    const locked = Boolean(m.locked);
                    const content = (
                      <>
                        <span className={`lesson-bullet ${l.lesson_id === lessonId && lesson.completed ? "done" : ""}`}>
                          {locked ? <Lock className="w-3 h-3" /> : l.type === "assignment" ? <FileText className="w-3 h-3" /> : l.type === "quiz" ? <ListChecks className="w-3 h-3" /> : (l.lesson_id === lessonId && lesson.completed ? <CheckCircle2 className="w-3 h-3" /> : <PlayCircle className="w-3 h-3" />)}
                        </span>
                        <span className="min-w-0 flex-1">
                          <span className="line-clamp-2">{l.title}</span>
                          {locked && (
                            <span className="mt-0.5 block text-[11px] text-gray-500">
                              Disponible dans {m.days_until_unlock || 1} jour{(m.days_until_unlock || 1) > 1 ? "s" : ""}
                            </span>
                          )}
                        </span>
                      </>
                    );
                    return locked ? (
                      <button key={l.lesson_id} type="button" disabled className="lesson-row w-full opacity-60 cursor-not-allowed" title={`Disponible le ${formatUnlockDate(m.unlock_at)}`} data-testid={`player-lesson-locked-${l.lesson_id}`}>
                        {content}
                      </button>
                    ) : (
                      <Link key={l.lesson_id} to={`/learn/${l.lesson_id}`} onClick={() => mobile && setMobileShowProgram(false)} className={`lesson-row ${l.lesson_id === lessonId ? "active" : ""}`} data-testid={`player-lesson-${l.lesson_id}`}>
                        {content}
                      </Link>
                    );
                  })}
                </div>
              )}
            </div>
          ))}
        </div>
      </aside>
  );

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[300px_1fr] gap-0 min-h-[calc(100vh-76px)]" data-testid="course-player">
      <ProgramSidebar />

      {mobileShowProgram ? (
        <div className="lg:hidden bg-brand-mint min-h-[calc(100vh-76px)]">
          <div className="max-w-4xl mx-auto p-5">
            <button
              type="button"
              onClick={() => setMobileShowProgram(false)}
              className="text-sm text-gray-500 inline-flex items-center gap-1 mb-4 hover:text-brand-green"
              data-testid="mobile-back-to-lesson"
            >
              <ChevronLeft className="w-4 h-4" /> Retour à la leçon
            </button>
            <ProgramSidebar mobile />
          </div>
        </div>
      ) : (
      <>
      {/* Main */}
      <div className="bg-brand-mint">
        <div className="max-w-4xl mx-auto p-5 sm:p-6 lg:p-10">
          <button
            type="button"
            onClick={() => setMobileShowProgram(true)}
            className="lg:hidden mb-5 inline-flex items-center gap-2 rounded-full border border-emerald-200 bg-white px-4 py-2 text-sm font-semibold text-brand-green shadow-sm"
            data-testid="mobile-show-program-btn"
          >
            <BookOpen className="w-4 h-4" /> Retour au programme
          </button>

          {/* Tabs */}
          <div className="flex items-center gap-1 mb-6 border-b border-emerald-200 overflow-x-auto" role="tablist">
            {TABS.map((t) => (
              <button key={t} role="tab" onClick={() => setActiveTab(t)} className={`px-4 py-3 text-sm font-semibold border-b-2 transition-colors whitespace-nowrap ${activeTab === t ? "border-brand-green text-brand-green" : "border-transparent text-gray-500 hover:text-black"}`} data-testid={`tab-${t}`}>
                {t === "contenu" ? "Contenu" : t === "note" ? "Prise de note" : t === "annexes" ? "Annexes" : `Commentaires (${comments.length})`}
              </button>
            ))}
          </div>

          {activeTab === "contenu" && (
            <div>
              <div className="flex items-start justify-between gap-4 mb-6 flex-wrap">
                <h1 className="font-heading text-3xl sm:text-4xl font-black text-brand-green flex-1" data-testid="lesson-title">{lesson.title}</h1>
                <button onClick={() => setAskHelpOpen(true)} className="btn-orange !px-4 !py-2 text-sm" data-testid="ask-community-help-btn" title="Pose ta question à la communauté">
                  <HelpCircle className="w-4 h-4" /> Demander de l'aide
                </button>
              </div>

              {askHelpOpen && (
                <div className="card p-5 mb-6 border-l-4 border-brand-orange bg-amber-50/50" data-testid="ask-help-panel">
                  <div className="flex items-center justify-between mb-3">
                    <p className="font-heading font-bold flex items-center gap-2"><HelpCircle className="w-5 h-5 text-brand-orange" /> Pose ta question à la communauté</p>
                    <button onClick={() => setAskHelpOpen(false)} className="text-gray-500 hover:text-black"><X className="w-4 h-4" /></button>
                  </div>
                  <p className="text-sm text-gray-600 mb-3">Ta question créera une discussion dans le channel <strong>FAQ</strong> avec le contexte de cette leçon. La communauté et le formateur pourront te répondre.</p>
                  <textarea
                    value={helpQuestion}
                    onChange={(e) => setHelpQuestion(e.target.value)}
                    rows={4}
                    placeholder="Décris précisément ta question ou ton blocage..."
                    className="input"
                    data-testid="ask-help-question-input"
                  />
                  <div className="flex gap-2 mt-3">
                    <button onClick={askCommunityHelp} disabled={!helpQuestion.trim() || helpSending} className="btn-primary" data-testid="ask-help-submit-btn">
                      <Send className="w-4 h-4" /> {helpSending ? "Envoi..." : "Publier dans la communauté"}
                    </button>
                    <button onClick={() => setAskHelpOpen(false)} className="btn-ghost">Annuler</button>
                  </div>
                  <p className="text-xs text-gray-500 mt-2">💡 Astuce : pour une question privée, utilise plutôt le bouton WhatsApp en bas à droite.</p>
                </div>
              )}

              <BlockRenderer blocks={lesson.blocks || []} />

              {/* Assignment block if lesson type = assignment */}
              {lesson.type === "assignment" && (() => {
                const settings = assignmentSettings(lesson);
                const submittedFiles = getSubmissionFiles(submission);
                const correctedFiles = getSubmissionFiles(submission, "corrected_file_urls", "corrected_file_url");
                const canDiscuss = submission && settings.enable_private_discussion && (submission.status !== "graded" || settings.enable_round_trips);

                return (
                  <div className="mt-10 card p-6" data-testid="assignment-section">
                    <h2 className="font-heading text-2xl font-bold text-brand-green mb-2 flex items-center gap-2">
                      <FileText className="w-6 h-6" /> Devoir à rendre
                    </h2>

                    {submission ? (
                      <div className="space-y-5">
                        <div className="flex flex-wrap items-center gap-2">
                          {submission.status === "graded" ? (
                            <span className="badge"><CheckCircle2 className="w-4 h-4" /> Corrigé</span>
                          ) : (
                            <span className="badge orange">En attente de correction</span>
                          )}
                          {settings.enable_rating ? <StarRating value={submission.rating} /> : submission.score !== null && submission.score !== undefined ? <span className="badge"><CheckCircle2 className="w-4 h-4" /> Note: {submission.score}/100</span> : null}
                          <p className="text-sm text-gray-600">Devoir rendu le {new Date(submission.submitted_at).toLocaleString("fr-FR")}.</p>
                        </div>

                        {submission.comment && (
                          <div className="bg-emerald-50 p-4 rounded-lg">
                            <p className="label mb-1">Retour du formateur</p>
                            <p className="text-sm whitespace-pre-line">{submission.comment}</p>
                          </div>
                        )}

                        {correctedFiles.length > 0 && (
                          <div>
                            <p className="label mb-2">Fichier(s) corrigé(s)</p>
                            <div className="flex flex-wrap gap-2">
                              {correctedFiles.map((url, index) => (
                                <a key={`${url}-${index}`} href={fileUrl(url)} target="_blank" rel="noopener noreferrer" className="btn-orange inline-flex" data-testid="download-corrected-btn">
                                  📥 Télécharger la correction {correctedFiles.length > 1 ? index + 1 : ""}
                                </a>
                              ))}
                            </div>
                          </div>
                        )}

                        {submission.text_response && (
                          <div className="bg-gray-50 p-4 rounded-lg text-sm">
                            <p className="label mb-1">Ta réponse</p>
                            <p className="whitespace-pre-line">{submission.text_response}</p>
                          </div>
                        )}

                        {submittedFiles.length > 0 && (
                          <div>
                            <p className="label mb-2">Fichier(s) rendu(s)</p>
                            <div className="flex flex-wrap gap-2">
                              {submittedFiles.map((url, index) => (
                                <a key={`${url}-${index}`} href={fileUrl(url)} target="_blank" rel="noopener noreferrer" className="text-brand-green underline text-sm">
                                  Voir le fichier {submittedFiles.length > 1 ? index + 1 : "rendu"}
                                </a>
                              ))}
                            </div>
                          </div>
                        )}

                        {settings.enable_private_discussion && (
                          <div className="rounded-2xl border border-emerald-100 bg-emerald-50/40 p-4">
                            <h3 className="font-heading text-xl font-bold mb-3 flex items-center gap-2"><MessageCircle className="w-5 h-5 text-brand-green" /> Discussion privée du devoir</h3>
                            <div className="space-y-3 mb-4 max-h-80 overflow-y-auto pr-1">
                              {(submission.messages || []).length === 0 && <p className="text-sm text-gray-500">Aucun message pour le moment.</p>}
                              {(submission.messages || []).map((m) => (
                                <div key={m.message_id} className={`rounded-2xl p-3 ${m.sender_user_id === user?.user_id ? "bg-white" : "bg-amber-50 border border-amber-100"}`}>
                                  <div className="flex items-center justify-between gap-3 mb-1">
                                    <p className="font-semibold text-sm">{m.sender_name || (m.sender_role === "admin" ? "Formateur" : "Apprenant")}</p>
                                    <p className="text-[11px] text-gray-500">{new Date(m.created_at).toLocaleString("fr-FR")}</p>
                                  </div>
                                  {m.body && <p className="text-sm whitespace-pre-line">{m.body}</p>}
                                  {(m.file_urls || []).length > 0 && (
                                    <div className="mt-2 flex flex-wrap gap-2">
                                      {m.file_urls.map((url, index) => (
                                        <a key={`${m.message_id}-${index}`} href={fileUrl(url)} target="_blank" rel="noopener noreferrer" className="text-xs font-semibold text-brand-green underline">Pièce jointe {index + 1}</a>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              ))}
                            </div>

                            {canDiscuss ? (
                              <div className="space-y-3">
                                <textarea value={assignmentMessage} onChange={(e) => setAssignmentMessage(e.target.value)} className="input" rows={3} placeholder="Répondre au formateur..." data-testid="assignment-private-message-input" />
                                <div className="flex flex-wrap items-center gap-2">
                                  <input
                                    ref={messageFileInputRef}
                                    type="file"
                                    multiple
                                    onChange={(e) => setAssignmentMessageFiles(Array.from(e.target.files || []))}
                                    className="hidden"
                                    data-testid="assignment-private-message-files-input"
                                  />
                                  <button type="button" onClick={() => messageFileInputRef.current?.click()} className="btn-ghost">
                                    <Upload className="w-4 h-4" /> {assignmentMessageFiles.length ? `${assignmentMessageFiles.length} fichier(s)` : "Joindre"}
                                  </button>
                                  <button onClick={sendAssignmentMessage} disabled={assignmentBusy || (!assignmentMessage.trim() && assignmentMessageFiles.length === 0)} className="btn-primary" data-testid="assignment-private-message-submit">
                                    <Send className="w-4 h-4" /> Envoyer
                                  </button>
                                </div>
                              </div>
                            ) : (
                              <p className="text-sm text-gray-500">La discussion privée est fermée pour ce devoir.</p>
                            )}
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="space-y-3">
                        <p className="text-sm text-gray-700">Rends ton travail en texte et/ou en pièce jointe.</p>
                        <textarea value={text} onChange={(e) => setText(e.target.value)} className="input" rows={5} placeholder="Ta réponse..." data-testid="assignment-text-input" />
                        <div>
                          <input
                            ref={fileInputRef}
                            type="file"
                            multiple={settings.allow_multiple_files}
                            onChange={handleFileChange}
                            className="hidden"
                            data-testid="assignment-file-input"
                          />
                          <button onClick={() => fileInputRef.current?.click()} className="btn-ghost" data-testid="assignment-file-btn">
                            <Upload className="w-4 h-4" /> {files.length ? `${files.length} fichier(s) sélectionné(s)` : settings.allow_multiple_files ? "Joindre un ou plusieurs fichiers" : "Joindre un fichier"}
                          </button>
                          {files.length > 0 && <p className="text-xs text-gray-500 mt-2">{files.map((f) => f.name).join(" · ")}</p>}
                        </div>
                        <button onClick={submitAssignment} disabled={assignmentBusy || (!text && files.length === 0)} className="btn-primary" data-testid="assignment-submit-btn">
                          <Send className="w-4 h-4" /> {assignmentBusy ? "Envoi..." : "Rendre le devoir"}
                        </button>
                      </div>
                    )}
                  </div>
                );
              })()}

              {lesson.type === "quiz" && (() => {
                const quiz = normalizeQuiz(lesson.quiz);
                const visibleQuestions = quiz.pagination ? quiz.questions.slice(quizPage, quizPage + 1) : quiz.questions;
                const isLastPage = quizPage >= quiz.questions.length - 1;
                const canSubmit = quiz.questions.length > 0 && quiz.questions.every((question) => (quizAnswers[question.id] || []).length > 0);

                return (
                  <div className="mt-10 card p-6" data-testid="quiz-player-section">
                    <h2 className="font-heading text-2xl font-bold text-brand-green mb-2 flex items-center gap-2">
                      <ListChecks className="w-6 h-6" /> Quiz
                    </h2>
                    <p className="text-sm text-gray-600 mb-5">
                      Taux minimum de réussite : <strong>{quiz.passingScore}%</strong>
                      {quiz.pagination ? " · 1 question par page" : ""}
                    </p>

                    {quiz.questions.length === 0 ? (
                      <p className="text-sm text-gray-500">Ce quiz ne contient pas encore de question.</p>
                    ) : (
                      <div className="space-y-5">
                        {quiz.pagination && (
                          <div className="flex items-center justify-between text-sm text-gray-600">
                            <span>Question {quizPage + 1} / {quiz.questions.length}</span>
                            <div className="h-2 flex-1 mx-4 rounded-full bg-emerald-100 overflow-hidden">
                              <div className="h-full bg-brand-green" style={{ width: `${((quizPage + 1) / quiz.questions.length) * 100}%` }} />
                            </div>
                          </div>
                        )}

                        {visibleQuestions.map((question, renderedIndex) => {
                          const realQuestionIndex = quiz.pagination ? quizPage : renderedIndex;
                          const resultDetail = quizResult?.details?.find((item) => item.questionId === question.id);

                          return (
                            <div key={question.id} className="rounded-2xl border border-emerald-100 bg-emerald-50/40 p-4" data-testid={`quiz-player-question-${realQuestionIndex}`}>
                              <p className="label mb-2">Question #{realQuestionIndex + 1}</p>
                              <h3 className="font-heading text-xl font-bold mb-4">{question.text || "Question sans titre"}</h3>
                              <div className="space-y-2">
                                {question.answers.map((answer) => {
                                  const selected = (quizAnswers[question.id] || []).includes(answer.id);
                                  const showCorrect = quizSubmitted && answer.isCorrect;
                                  const showWrong = quizSubmitted && selected && !answer.isCorrect;

                                  return (
                                    <button
                                      key={answer.id}
                                      type="button"
                                      onClick={() => toggleQuizAnswer(question, answer.id)}
                                      className={`w-full flex items-center gap-3 rounded-xl border p-3 text-left transition ${selected ? "border-brand-green bg-white" : "border-emerald-100 bg-white/70 hover:bg-white"} ${showCorrect ? "!border-brand-green !bg-emerald-50" : ""} ${showWrong ? "!border-red-300 !bg-red-50" : ""}`}
                                      disabled={quizSubmitted}
                                      data-testid={`quiz-player-answer-${answer.id}`}
                                    >
                                      <span className={`w-5 h-5 flex items-center justify-center ${question.type === "multiple" ? "rounded" : "rounded-full"} border ${selected ? "border-brand-green bg-brand-green text-white" : "border-gray-300"}`}>
                                        {selected ? "✓" : ""}
                                      </span>
                                      <span className="flex-1">{answer.text || "Réponse vide"}</span>
                                      {quizSubmitted && showCorrect && <span className="text-xs font-semibold text-brand-green">Bonne réponse</span>}
                                      {quizSubmitted && showWrong && <span className="text-xs font-semibold text-red-600">Ta réponse</span>}
                                    </button>
                                  );
                                })}
                              </div>
                              {quizSubmitted && resultDetail && (
                                <p className={`mt-3 text-sm font-semibold ${resultDetail.correct ? "text-brand-green" : "text-red-600"}`}>
                                  {resultDetail.correct ? "Correct" : "Incorrect"}
                                </p>
                              )}
                            </div>
                          );
                        })}

                        {quizResult && (
                          <div className={`rounded-2xl border p-4 ${quizResult.passed ? "border-emerald-200 bg-emerald-50" : "border-red-200 bg-red-50"}`} data-testid="quiz-result-box">
                            <p className="font-heading text-xl font-bold mb-1">
                              {quizResult.passed ? "Quiz réussi" : "Quiz non validé"}
                            </p>
                            <p className="text-sm">
                              Score : <strong>{quizResult.score}%</strong> — {quizResult.correctCount}/{quizResult.total} bonne(s) réponse(s).
                            </p>
                            {!quizResult.passed && <p className="text-sm mt-1">Tu peux relire la leçon puis réessayer.</p>}
                          </div>
                        )}

                        <div className="flex items-center justify-between gap-3 flex-wrap">
                          {quiz.pagination && !quizSubmitted ? (
                            <>
                              <button type="button" onClick={() => setQuizPage((page) => Math.max(0, page - 1))} disabled={quizPage === 0} className="btn-ghost disabled:opacity-40">Précédent</button>
                              {!isLastPage ? (
                                <button type="button" onClick={() => setQuizPage((page) => Math.min(quiz.questions.length - 1, page + 1))} className="btn-primary">Question suivante</button>
                              ) : (
                                <button type="button" onClick={submitQuiz} disabled={!canSubmit} className="btn-primary disabled:opacity-40" data-testid="quiz-submit-btn">Terminer le quiz</button>
                              )}
                            </>
                          ) : !quizSubmitted ? (
                            <button type="button" onClick={submitQuiz} disabled={!canSubmit} className="btn-primary disabled:opacity-40" data-testid="quiz-submit-btn">Terminer le quiz</button>
                          ) : (
                            <button type="button" onClick={resetQuiz} className="btn-ghost" data-testid="quiz-retry-btn">Réessayer</button>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })()}

              <div className="mt-10 flex items-center justify-between gap-4 flex-wrap">
                {prev && !prev.locked ? <Link to={`/learn/${prev.lesson_id}`} className="btn-ghost" data-testid="prev-lesson-btn"><ChevronLeft className="w-4 h-4" /> Leçon précédente</Link> : <span />}
                <button onClick={markComplete} className="btn-primary" data-testid="mark-complete-btn">
                  <CheckCircle2 className="w-4 h-4" /> {lesson.completed ? "Leçon terminée" : "Marquer comme terminée"}
                </button>
                {next && !next.locked ? <Link to={`/learn/${next.lesson_id}`} className="btn-ghost" data-testid="next-lesson-btn">Leçon suivante <ChevronLeft className="w-4 h-4 rotate-180" /></Link> : next?.locked ? <span className="text-xs text-gray-500 flex items-center gap-1"><Lock className="w-3.5 h-3.5" /> Prochaine leçon disponible dans {next.days_until_unlock || 1} jour{(next.days_until_unlock || 1) > 1 ? "s" : ""}</span> : <span />}
              </div>
            </div>
          )}

          {activeTab === "note" && (
            <div data-testid="note-tab">
              <h2 className="font-heading text-2xl font-bold mb-3">Prise de note personnelle</h2>
              <p className="text-sm text-gray-600 mb-4">Cette note est privée. Tu peux y revenir à tout moment.</p>
              <textarea value={note} onChange={(e) => setNote(e.target.value)} onBlur={saveNote} className="input min-h-[300px]" placeholder="Écris ici tes idées, déclics, plan d'action..." data-testid="note-textarea" />
              <p className="text-xs text-gray-500 mt-2">Sauvegarde automatique en quittant la zone de saisie.</p>
            </div>
          )}

          {activeTab === "annexes" && (
            <div data-testid="annexes-tab">
              <h2 className="font-heading text-2xl font-bold mb-3">Annexes & ressources</h2>
              {((lesson.blocks || []).filter((b) => b.type === "file").length > 0) ? (
                <ul className="space-y-2">
                  {(lesson.blocks || []).filter((b) => b.type === "file").map((b) => (
                    <li key={b.id}>
                      <a href={fileUrl(b.content?.url)} target="_blank" rel="noopener noreferrer" className="card p-4 flex items-center gap-2 hover:bg-emerald-50">📎 {b.content?.name || "Fichier"}</a>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-sm text-gray-500">Aucune annexe pour cette leçon.</p>
              )}
            </div>
          )}

          {activeTab === "commentaires" && (
            <div data-testid="comments-tab">
              <h2 className="font-heading text-2xl font-bold mb-4 flex items-center gap-2"><MessageCircle className="w-5 h-5 text-brand-green" /> Commentaires</h2>
              <div className="card p-4 mb-6 border-amber-100 bg-amber-50/40" data-testid="lesson-feedback-rating">
                <p className="font-heading font-bold mb-1">Ton avis rapide sur cette leçon</p>
                <p className="text-sm text-gray-600 mb-3">Note la clarté et l’utilité de la leçon. Ça m’aide à améliorer la formation.</p>
                <div className="flex items-center gap-2 flex-wrap">
                  {[1, 2, 3, 4, 5].map((rating) => (
                    <button
                      key={rating}
                      type="button"
                      onClick={() => saveLessonRating(rating)}
                      className={`rounded-full p-1 transition ${lessonRating >= rating ? "text-amber-500 scale-110" : "text-gray-300 hover:text-amber-400"}`}
                      aria-label={`Noter ${rating} sur 5`}
                      data-testid={`lesson-rating-star-${rating}`}
                    >
                      <Star className="w-8 h-8 fill-current" />
                    </button>
                  ))}
                  <span className="text-sm font-semibold text-gray-600 ml-1">{lessonRating ? `${lessonRating}/5` : "Pas encore notée"}</span>
                  {ratingSaved && <span className="text-xs rounded-full bg-emerald-100 text-emerald-700 px-3 py-1 font-semibold">Avis enregistré</span>}
                </div>
              </div>
              <div className="space-y-3 mb-6">
                {comments.length === 0 && <p className="text-sm text-gray-500">Sois le premier à commenter cette leçon.</p>}
                {comments.map((c) => (
                  <div key={c.comment_id} className="card p-4">
                    <div className="flex items-center gap-3 mb-1">
                      <div className="avatar !w-8 !h-8 text-xs">{(c.user_name || "?").slice(0, 1).toUpperCase()}</div>
                      <div>
                        <p className="font-semibold text-sm">{c.user_name}</p>
                        <p className="text-xs text-gray-500">{new Date(c.created_at).toLocaleString("fr-FR")}</p>
                      </div>
                    </div>
                    <p className="text-sm whitespace-pre-line ml-11">{c.text}</p>
                  </div>
                ))}
              </div>
              <div className="card p-4">
                <textarea value={comment} onChange={(e) => setComment(e.target.value)} className="input" rows={3} placeholder="Ton commentaire..." data-testid="comment-input" />
                <div className="text-right mt-2">
                  <button onClick={sendComment} disabled={!comment.trim()} className="btn-primary !px-4 !py-2 text-sm" data-testid="comment-submit-btn">
                    Envoyer
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
      </>
      )}
    </div>
  );
}